import { useMemo } from 'react';
import { useGraphIndex } from './useGraphIndex';

export type SearchHitKind = 'kural' | 'adhigaram' | 'theme';

export interface SearchHit {
  kind: SearchHitKind;
  id: number | string;
  primary: string;      // English label
  primaryTa?: string;   // Tamil label (if any)
  sub?: string;         // gist or other short subtitle
  keywords: string;     // pre-normalised searchable haystack
}

function normalise(s: string): string {
  return s.toLowerCase().normalize('NFC').trim();
}

/**
 * Build the search index once from graph.json. All searchable strings
 * are normalised (lower + NFC + trim) up front so the runtime filter
 * is a plain substring compare — no fuzzy library, works on Tamil input.
 */
export function useSearchIndex(): SearchHit[] {
  const graph = useGraphIndex();
  return useMemo<SearchHit[]>(() => {
    if (!graph.data) return [];
    const out: SearchHit[] = [];

    // Kurals: number + first_line_ta + gist_en + transliteration variants.
    for (const k of graph.data.kurals) {
      out.push({
        kind: 'kural',
        id: k.n,
        primary: `Kural ${k.n}`,
        primaryTa: k.first_line_ta,
        sub: k.gist_en,
        keywords: normalise(
          `kural ${k.n} ${k.first_line_ta} ${k.first_line_ta_translit} ${k.gist_en}`
        ),
      });
    }

    // Adhigarams: title_en + title_ta + transliteration.
    for (const a of graph.data.adhigarams) {
      out.push({
        kind: 'adhigaram',
        id: a.n,
        primary: a.title_en,
        primaryTa: a.title_ta,
        sub: `Adhigaram ${a.n} · ${a.iyal_en}`,
        keywords: normalise(
          `adhigaram ${a.n} ${a.title_en} ${a.title_ta} ${a.title_ta_translit} ${a.iyal_en}`
        ),
      });
    }

    // Themes: title_en + title_ta + transliteration.
    for (const t of graph.data.themes) {
      out.push({
        kind: 'theme',
        id: t.slug,
        primary: t.title_en,
        primaryTa: t.title_ta,
        sub: t.description,
        keywords: normalise(
          `theme ${t.title_en} ${t.title_ta} ${t.title_ta_translit} ${t.slug}`
        ),
      });
    }

    return out;
  }, [graph.data]);
}

const MAX_PER_GROUP = 20;

/**
 * Filter the index for a substring query. Results are grouped by kind
 * (Kural / Adhigaram / Theme). Each group is capped at ``MAX_PER_GROUP``
 * so a very common substring can't blow up the DOM.
 */
export function filterSearch(
  index: SearchHit[],
  query: string
): { kurals: SearchHit[]; adhigarams: SearchHit[]; themes: SearchHit[] } {
  const q = normalise(query);
  const empty = { kurals: [], adhigarams: [], themes: [] };
  if (q.length === 0) return empty;
  const kurals: SearchHit[] = [];
  const adhigarams: SearchHit[] = [];
  const themes: SearchHit[] = [];
  for (const h of index) {
    if (!h.keywords.includes(q)) continue;
    if (h.kind === 'kural' && kurals.length < MAX_PER_GROUP) kurals.push(h);
    else if (h.kind === 'adhigaram' && adhigarams.length < MAX_PER_GROUP)
      adhigarams.push(h);
    else if (h.kind === 'theme' && themes.length < MAX_PER_GROUP) themes.push(h);
    if (
      kurals.length === MAX_PER_GROUP &&
      adhigarams.length === MAX_PER_GROUP &&
      themes.length === MAX_PER_GROUP
    ) {
      break;
    }
  }
  return { kurals, adhigarams, themes };
}
